import { InputElement } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { IPriceUnderProductSchemaUI } from '@monetize/types/app';
import { Fragment } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdInfo } from 'react-icons/md';
import z from 'zod';
import { PRODUCT_TYPE_DISPLAY_SHORT } from '../../../constants/products';
import {
  MBox,
  MButton,
  MCheckbox,
  MCustomNumberInput,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MIcon,
  MText,
  MTooltip,
} from '../../Monetize';

const FormSchema = z
  .object({
    applyToAll: z.boolean(),
    percentage: z.union([z.number().min(-100), z.string(), z.null()]),
    products: z
      .object({
        productId: z.string(),
        productName: z.string(),
        percentage: z.union([z.number().min(-100), z.string(), z.null()]),
      })
      .array(),
  })
  .refine((val) => {
    if (val.applyToAll) {
      return val.percentage !== null && val.percentage !== '';
    }
    return val.products.some(
      (product) => product.percentage !== null && product.percentage !== '',
    );
  });
type Form = z.infer<typeof FormSchema>;
export type BulkPriceChange = { productId: string; percentage: number };

function getFormValues(
  selectedProductsWithPrices: IPriceUnderProductSchemaUI[],
): Form {
  return {
    applyToAll: true,
    percentage: '',
    products: selectedProductsWithPrices.map((product) => ({
      productId: product.id,
      productName: product.name,
      percentage: '',
    })),
  };
}

interface PricesDrawerBulkEditPricesProps {
  selectedProductsWithPrices: IPriceUnderProductSchemaUI[];
  onSubmit: (data: BulkPriceChange[]) => void;
}

export const PricesDrawerBulkEditPrices = ({
  selectedProductsWithPrices,
  onSubmit,
}: PricesDrawerBulkEditPricesProps) => {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: getFormValues(selectedProductsWithPrices),
  });

  const applyToAll = watch('applyToAll');

  function handleFormSubmit(data: Form) {
    if (data.applyToAll) {
      // Apply the same percentage to all products
      const percentage = Number(data.percentage) / 100;
      const result: BulkPriceChange[] = selectedProductsWithPrices.map(
        (product) => ({
          productId: product.id,
          percentage,
        }),
      );
      onSubmit(result);
    } else {
      // Apply individual percentages
      const result: BulkPriceChange[] = data.products.map((product) => ({
        productId: product.productId,
        percentage: Number(product.percentage) / 100,
      }));
      onSubmit(result);
    }
  }

  return (
    <MBox w="full" mx={4}>
      <MGrid templateColumns="1fr 200px" alignItems="center" rowGap={4} py={2}>
        <MGridItem />
        <MGridItem display="flex">
          <MText fontWeight="semibold">Price Change %</MText>
          <MTooltip
            label={
              <MBox>
                <MText color="tWhite.base" fontSize="xs" mb={2}>
                  Use a positive number to increase prices or a negative number
                  to decrease prices.
                </MText>
                <MText color="tWhite.base" fontSize="xs">
                  This creates a copy of the rate just for this account and
                  doesn't change the standard rate.
                </MText>
                <MText>
                  To apply another percentage change in the future after saving,
                  switch back to the standard rate and apply a new adjustment.
                </MText>
              </MBox>
            }
            placement="top"
          >
            <MFlex alignItems="center">
              <MIcon as={MdInfo} color="tPurple.base" mx="2" />
            </MFlex>
          </MTooltip>
        </MGridItem>

        <MGridItem>
          <Controller
            control={control}
            name="applyToAll"
            render={({ field: { value, ...rest } }) => (
              <MCheckbox isChecked={value} {...rest}>
                Apply to all products
              </MCheckbox>
            )}
          />
        </MGridItem>
        <MGridItem>
          <MBox pr={12}>
            <Controller
              control={control}
              name="percentage"
              render={({ field: { value, onChange, ...rest } }) => (
                <MFormField error={errors.percentage}>
                  <MBox position="relative">
                    <MCustomNumberInput
                      isDisabled={!applyToAll}
                      inputMode="decimal"
                      min={-100}
                      value={value ?? ''}
                      onChange={(newValue) => {
                        onChange(newValue);
                      }}
                      {...rest}
                    />
                    {/* v2's InputGroup sized this as a 30px square at the input's
                        edge; v3's InputElement only gets a `top` inside a group. */}
                    <InputElement
                      placement="end"
                      top="0"
                      w="30px"
                      px="0"
                      fontWeight="bold"
                    >
                      %
                    </InputElement>
                  </MBox>
                </MFormField>
              )}
            />
          </MBox>
        </MGridItem>
        {selectedProductsWithPrices.map((product, i) => (
          <Fragment key={product.id}>
            <MGridItem>
              <MText as="h5" fontSize="sm" fontWeight="bold">
                {product.name}{' '}
                <MText as="span" fontWeight="normal">
                  ({PRODUCT_TYPE_DISPLAY_SHORT[product.productType]})
                </MText>
              </MText>
            </MGridItem>
            <MGridItem>
              <MBox pr={12}>
                <Controller
                  control={control}
                  name={`products.${i}.percentage`}
                  render={({ field: { value, onChange, ...rest } }) => (
                    <MFormField error={errors.products?.[i]}>
                      <MBox position="relative">
                        <MCustomNumberInput
                          isDisabled={applyToAll}
                          inputMode="decimal"
                          min={-100}
                          value={value ?? ''}
                          onChange={(newValue) => {
                            onChange(newValue);
                          }}
                          {...rest}
                        />
                        <InputElement
                          placement="end"
                          top="0"
                          w="30px"
                          px="0"
                          fontWeight="bold"
                        >
                          %
                        </InputElement>
                      </MBox>
                    </MFormField>
                  )}
                />
              </MBox>
            </MGridItem>
          </Fragment>
        ))}
        <MGridItem colSpan={2} textAlign="right" pt={4} mr={12}>
          <MButton
            type="button"
            isDisabled={!isValid}
            onClick={handleSubmit(handleFormSubmit)}
          >
            Apply Price Changes
          </MButton>
        </MGridItem>
      </MGrid>
    </MBox>
  );
};
